import { Alert } from '@ksyos/design-system';
import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { IFileUpload } from '@frontend/components/interfaces';
import { imageAndPdfExtensions } from '@common/utils/fileExtensions';
import { getSelectedLanguage } from '@frontend/translate';
import type { IOphthalmologyAsset } from '../../../../../../common/validation/customControls/ophthalmology';
import type { IServiceGetOne } from '../../../../interfaces/serviceGetOne';
import type { IProcedureAddEdit } from '../../../procedures/ProcedureItemAddEdit';
import { FilesUrlBase } from '../../../../../../common/enums/filesUrlBase';
import { OphthalmologyFilePlacement } from '../../../../../../common/validation/customControls/ophthalmology';
import { FlexColumns } from '../../../../elements/FlexColumns';
import { FormGroup } from '../../../../elements/forms/FormGroup';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { AssetDropzoneWithActions } from '../../utils/v1/AssetDropzoneWithActions';
import { getFilesByPlacement } from './getFilesByPlacement';

const placeholder = "Klik of sleep je foto's in dit vak";

export interface IOphthalmologyDropzoneProcedureAddEditProps {
    assets: IOphthalmologyAsset[];
    handleChangeCustomControl: (values: { files: IOphthalmologyAsset[] }) => Promise<void>;
    isAutoSaveActive: boolean;
    isBsnValidationEnabled?: boolean;
    maxFiles?: number;
    onUploading: (isUploading: boolean) => Promise<void>;
    patientBsn?: string;
    procedure: IProcedureAddEdit;
    service: IServiceGetOne;
}

export function OphthalmologyDropzoneProcedureAddEdit(props: IOphthalmologyDropzoneProcedureAddEditProps) {
    const [group] = useState<string>(uuidv4());
    const [showBsnValidationMessage, setShowBsnValidationMessage] = useState<boolean>(false);

    const language = getSelectedLanguage();

    return (
        <FormGroup>
            <FlexColumns sm={8}>
                <>
                    <FormLabel stacked>OD</FormLabel>
                    <AssetDropzoneWithActions
                        acceptedFiles={imageAndPdfExtensions}
                        assets={getFilesByPlacement(props.assets, OphthalmologyFilePlacement.od)}
                        assetsBaseUrl={FilesUrlBase.procedures}
                        automation="dropzoneOd"
                        cleanDropzoneAfterCompletingFiles={props.isAutoSaveActive}
                        group={group}
                        maxFiles={props.maxFiles ?? 4}
                        meetsCustomAssetValidation={props.isBsnValidationEnabled ? isBsnInFilename : undefined}
                        metadata={{ placement: OphthalmologyFilePlacement.od }}
                        onChange={async (assets) => {
                            const files: IOphthalmologyAsset[] = assets.map((asset) => {
                                return { ...asset, placement: OphthalmologyFilePlacement.od };
                            });
                            await props.handleChangeCustomControl({
                                files: [
                                    ...files,
                                    ...getFilesByPlacement(props.assets, OphthalmologyFilePlacement.os),
                                    ...getFilesByPlacement(props.assets, OphthalmologyFilePlacement.aanvullend),
                                    ...getFilesByPlacement(props.assets, OphthalmologyFilePlacement.verwijsbrief),
                                ],
                            });
                        }}
                        onFileAccepted={async () => await props.onUploading(true)}
                        onUploading={props.onUploading}
                        placeholder={placeholder}
                        processActivityId={props.procedure.id}
                        service={props.service}
                    />
                </>
                <>
                    <FormLabel stacked>OS</FormLabel>
                    <AssetDropzoneWithActions
                        acceptedFiles={imageAndPdfExtensions}
                        assets={getFilesByPlacement(props.assets, OphthalmologyFilePlacement.os)}
                        assetsBaseUrl={FilesUrlBase.procedures}
                        automation="dropzoneOs"
                        cleanDropzoneAfterCompletingFiles={props.isAutoSaveActive}
                        group={group}
                        maxFiles={props.maxFiles ?? 4}
                        meetsCustomAssetValidation={props.isBsnValidationEnabled ? isBsnInFilename : undefined}
                        metadata={{ placement: OphthalmologyFilePlacement.os }}
                        onChange={async (assets) => {
                            const files: IOphthalmologyAsset[] = assets.map((asset) => {
                                return { ...asset, placement: OphthalmologyFilePlacement.os };
                            });
                            await props.handleChangeCustomControl({
                                files: [
                                    ...getFilesByPlacement(props.assets, OphthalmologyFilePlacement.od),
                                    ...files,
                                    ...getFilesByPlacement(props.assets, OphthalmologyFilePlacement.aanvullend),
                                    ...getFilesByPlacement(props.assets, OphthalmologyFilePlacement.verwijsbrief),
                                ],
                            });
                        }}
                        onFileAccepted={async () => await props.onUploading(true)}
                        onUploading={props.onUploading}
                        placeholder={placeholder}
                        processActivityId={props.procedure.id}
                        service={props.service}
                    />
                </>
                <>
                    <FormLabel stacked>Aanvullend</FormLabel>
                    <AssetDropzoneWithActions
                        acceptedFiles={imageAndPdfExtensions}
                        assets={getFilesByPlacement(props.assets, OphthalmologyFilePlacement.aanvullend)}
                        assetsBaseUrl={FilesUrlBase.procedures}
                        automation="dropzoneAdditional"
                        cleanDropzoneAfterCompletingFiles={props.isAutoSaveActive}
                        group={group}
                        maxFiles={props.maxFiles ?? 4}
                        meetsCustomAssetValidation={props.isBsnValidationEnabled ? isBsnInFilename : undefined}
                        metadata={{ placement: OphthalmologyFilePlacement.aanvullend }}
                        onChange={async (assets) => {
                            const files: IOphthalmologyAsset[] = assets.map((asset) => {
                                return { ...asset, placement: OphthalmologyFilePlacement.aanvullend };
                            });
                            await props.handleChangeCustomControl({
                                files: [
                                    ...getFilesByPlacement(props.assets, OphthalmologyFilePlacement.od),
                                    ...getFilesByPlacement(props.assets, OphthalmologyFilePlacement.os),
                                    ...files,
                                    ...getFilesByPlacement(props.assets, OphthalmologyFilePlacement.verwijsbrief),
                                ],
                            });
                        }}
                        onFileAccepted={async () => await props.onUploading(true)}
                        onUploading={props.onUploading}
                        placeholder={placeholder}
                        processActivityId={props.procedure.id}
                        service={props.service}
                    />
                </>
            </FlexColumns>
            {showBsnValidationMessage && (
                <Alert
                    appearance="warning"
                    hasIcon={true}
                >
                    {language.ophthalmology.noBsnMatchingValidationMessage}
                </Alert>
            )}
        </FormGroup>
    );

    function isBsnInFilename(file: IFileUpload) {
        const isBsnWithinFilename = file.name.includes(props.patientBsn as string);
        setShowBsnValidationMessage(!isBsnWithinFilename);

        return isBsnWithinFilename;
    }
}
